import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UsageEventsTracker } from '../../../services/UsageEventsTracker';

/* ── Year options ── */
const YEAR_OPTIONS = [
  { value: 'freshman',  label: 'Freshman' },
  { value: 'sophomore', label: 'Sophomore' },
  { value: 'junior',    label: 'Junior' },
  { value: 'senior',    label: 'Senior' },
  { value: 'graduate',  label: 'Graduate' },
  { value: 'other',     label: 'Other' },
];

const SUB_STEPS = ['school', 'year', 'courses'];

/* ── Glow intensity per step ── */
const GLOW_SCALE = [1, 1.15, 1.3];

/* ── Enhanced slide variants: exit blurs, enter slides from right ── */
const slideVariants = {
  enter: (dir) => ({
    opacity: 0,
    x: dir > 0 ? 60 : -60,
    filter: 'blur(0px)',
  }),
  center: {
    opacity: 1,
    x: 0,
    filter: 'blur(0px)',
  },
  exit: (dir) => ({
    opacity: 0,
    x: dir > 0 ? -40 : 40,
    filter: 'blur(6px)',
  }),
};

/**
 * Step 2 — Conversational Setup
 * Two-column layout: Scout on left, question on right.
 * No Continue button — Enter / click-to-select drives progression.
 */
export default function ProfileStep({ data, updateData, goNext, goBack }) {
  const [subStep, setSubStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isTyping, setIsTyping] = useState(false);
  const schoolInputRef = useRef(null);
  const coursesWrapRef = useRef(null);
  const fieldFocusOrderRef = useRef([]);
  const typingTimeoutRef = useRef(null);

  const trackFieldFocus = useCallback((field) => {
    if (!fieldFocusOrderRef.current.includes(field)) {
      fieldFocusOrderRef.current.push(field);
      UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
        action: 'field_focus',
        category: 'onboarding',
        label: 'profile',
        metadata: { field, focusOrder: fieldFocusOrderRef.current.length },
      });
    }
  }, []);

  // Auto-focus inputs when sub-step changes
  // Delay must exceed AnimatePresence exit duration (380ms) so the new element is mounted
  useEffect(() => {
    if (subStep === 0) {
      setTimeout(() => schoolInputRef.current?.focus(), 500);
    }
    if (subStep === 2) {
      setTimeout(() => coursesWrapRef.current?.focus(), 500);
    }
  }, [subStep]);

  // Clean up typing timeout on unmount
  useEffect(() => () => { clearTimeout(typingTimeoutRef.current); }, []);

  const goSubNext = useCallback(() => {
    if (subStep < SUB_STEPS.length - 1) {
      setDirection(1);
      setSubStep(s => s + 1);
    } else {
      goNext();
    }
  }, [subStep, goNext]);

  const goSubBack = useCallback(() => {
    if (subStep > 0) {
      setDirection(-1);
      setSubStep(s => s - 1);
    } else {
      goBack();
    }
  }, [subStep, goBack]);

  // Typing indicator — fires on every keystroke, debounces off
  const handleSchoolChange = (e) => {
    updateData({ school: e.target.value });
    setIsTyping(true);
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 600);
  };

  // Enter key on school input → advance
  const handleSchoolKeyDown = (e) => {
    if (e.key === 'Enter' && data.school?.trim()) {
      setIsTyping(false);
      UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
        action: 'field_completed',
        category: 'onboarding',
        label: 'profile',
        metadata: { field: 'school', hasValue: true },
      });
      goSubNext();
    }
  };

  // Year selection → auto-advance
  const handleYearSelect = (val) => {
    trackFieldFocus('year');
    updateData({ year: val });
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'field_completed',
      category: 'onboarding',
      label: 'profile',
      metadata: { field: 'year', value: val },
    });
    setTimeout(() => {
      setDirection(1);
      setSubStep(s => s + 1);
    }, 250);
  };

  // Course count — Enter key to advance
  const handleCoursesKeyDown = (e) => {
    if (e.key === 'Enter') goSubNext();
  };

  const courseCount = data.courseCount ?? 4;

  // Memoised glow scale for current step
  const glowScale = useMemo(() => GLOW_SCALE[subStep] ?? 1, [subStep]);

  const renderQuestion = () => {
    switch (SUB_STEPS[subStep]) {
      case 'school':
        return (
          <motion.div
            key="school"
            className="scout-conv-question"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.h2
              className="scout-conv-prompt"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
            >
              Where are you studying?
            </motion.h2>

            <motion.div
              className="scout-conv-input-wrap"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            >
              <input
                ref={schoolInputRef}
                type="text"
                className={`scout-conv-input${data.school?.trim() ? ' has-value' : ''}`}
                placeholder="Type your school name…"
                value={data.school || ''}
                onFocus={() => trackFieldFocus('school')}
                onChange={handleSchoolChange}
                onKeyDown={handleSchoolKeyDown}
                autoComplete="off"
              />
              <AnimatePresence>
                {data.school?.trim() && (
                  <motion.span
                    className="scout-conv-enter-hint"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    Press Enter ↵
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        );

      case 'year':
        return (
          <motion.div
            key="year"
            className="scout-conv-question"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.h2
              className="scout-conv-prompt"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
            >
              What year are you in?
            </motion.h2>

            <div className="scout-conv-tiles" role="radiogroup" aria-label="Academic year">
              {YEAR_OPTIONS.map((opt, idx) => (
                <motion.button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={data.year === opt.value}
                  className={`scout-conv-tile${data.year === opt.value ? ' selected' : ''}`}
                  onClick={() => handleYearSelect(opt.value)}
                  initial={{ opacity: 0, y: 16, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    duration: 0.32,
                    delay: 0.1 + 0.045 * idx,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  whileTap={{ scale: 0.93 }}
                  whileHover={{ scale: 1.03, y: -2 }}
                >
                  {opt.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        );

      case 'courses':
        return (
          <motion.div
            key="courses"
            className="scout-conv-question"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            onKeyDown={handleCoursesKeyDown}
            tabIndex={-1}
            ref={(node) => {
              coursesWrapRef.current = node;
              if (node) setTimeout(() => node.focus(), 50);
            }}
          >
            <motion.h2
              className="scout-conv-prompt"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
            >
              How many classes this semester?
            </motion.h2>

            <motion.div
              className="scout-conv-stepper"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.button
                type="button"
                className="scout-conv-stepper-btn"
                onClick={() => {
                  const next = Math.max(1, courseCount - 1);
                  updateData({ courseCount: next });
                  UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
                    action: 'course_count_change',
                    category: 'onboarding',
                    label: 'profile',
                    metadata: { from: courseCount, to: next },
                  });
                }}
                disabled={courseCount <= 1}
                aria-label="Decrease course count"
                whileTap={{ scale: 0.85 }}
              >
                −
              </motion.button>

              <motion.span
                key={courseCount}
                className="scout-conv-stepper-value"
                aria-live="polite"
                initial={{ scale: 0.8, opacity: 0.4 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 18,
                }}
              >
                {courseCount}
              </motion.span>

              <motion.button
                type="button"
                className="scout-conv-stepper-btn"
                onClick={() => {
                  const next = Math.min(10, courseCount + 1);
                  updateData({ courseCount: next });
                  UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
                    action: 'course_count_change',
                    category: 'onboarding',
                    label: 'profile',
                    metadata: { from: courseCount, to: next },
                  });
                }}
                disabled={courseCount >= 10}
                aria-label="Increase course count"
                whileTap={{ scale: 0.85 }}
              >
                +
              </motion.button>
            </motion.div>

            <motion.span
              className="scout-conv-enter-hint"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.35 }}
            >
              Press Enter ↵
            </motion.span>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="scout-conv-wrapper">
      {/* Background accents */}
      <div className="scout-conv-bg-glow" aria-hidden="true" />
      <div className="scout-conv-bg-gradient" aria-hidden="true" />

      {/* Back link */}
      <motion.button
        className="scout-conv-back"
        onClick={goSubBack}
        type="button"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        whileHover={{ opacity: 0.85 }}
      >
        ← Back
      </motion.button>

      <div className="scout-conv-layout">
        {/* Left: Scout */}
        <motion.div
          className="scout-conv-left"
          initial={{ opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Glow ring — animates intensity per step */}
          <motion.div
            className="scout-conv-scout-glow"
            aria-hidden="true"
            animate={{ scale: glowScale, opacity: 0.6 + (glowScale - 1) * 1.2 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />

          {/* Scout image — slow float */}
          <motion.img
            src="/scout_images/scout_thinking.png"
            alt="Scout thinking"
            className="scout-conv-scout-img"
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Typing feedback dots — visible while user types */}
          <AnimatePresence>
            {isTyping && (
              <motion.div
                className="scout-conv-typing-dots"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.2 }}
                aria-hidden="true"
              >
                <span className="scout-conv-dot" />
                <span className="scout-conv-dot" />
                <span className="scout-conv-dot" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Light spill toward text area */}
          <div className="scout-conv-light-spill" aria-hidden="true" />
        </motion.div>

        {/* Right: Question */}
        <div className="scout-conv-right">
          <AnimatePresence mode="wait" custom={direction}>
            {renderQuestion()}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
